import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Eye, AlertCircle, RefreshCw, X } from "lucide-react";
import { addTournament, deleteTournamentDetails, getAllPartners, getTournamentById, getTournaments, updateTournament, uploadFile } from "../service/api";
import { toast } from "sonner";
import { format, parseISO, isValid } from "date-fns";
import { DatePicker } from "../components/ui/date-picker";
import { TimePicker } from "../components/ui/time-picker";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { TablePagination } from "../components/ui/pagination";

function normalizeBannerImages(raw: unknown): { imageUrl: string }[] {
    if (raw == null) return [];
    if (Array.isArray(raw)) {
        return raw.map((item) => {
            if (item == null) return { imageUrl: "" };
            if (typeof item === "string") return { imageUrl: item };
            if (typeof item === "object" && item !== null && "imageUrl" in item) {
                return { imageUrl: String((item as { imageUrl: unknown }).imageUrl) };
            }
            return { imageUrl: String(item) };
        }).filter((x) => x.imageUrl.length > 0);
    }
    if (typeof raw === "string") {
        const s = raw.trim();
        if (!s) return [];
        try { return normalizeBannerImages(JSON.parse(s)); } catch { return [{ imageUrl: s }]; }
    }
    return [];
}

function viewGalleryImages(raw: unknown): string[] {
    if (raw == null) return [];
    if (Array.isArray(raw)) return raw.map((x) => (typeof x === "string" ? x : String(x))).filter(Boolean);
    if (typeof raw === "string") {
        try { return viewGalleryImages(JSON.parse(raw)); } catch { return raw.trim() ? [raw.trim()] : []; }
    }
    return [];
}

const Tournament = () => {
    const [mode, setMode] = useState('add');
    const [addModal, setAddModal] = useState(false);
    const [tableLoading, setTableLoading] = useState(false);
    const [partnersLoading, setPartnersLoading] = useState(false);
    const [tournaments, setTournaments] = useState<any>([]);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [partners, setPartners] = useState<any>([]);
    const [bannerImage, setBannerImage] = useState<{ imageUrl: string }[]>([]);
    const [limit] = useState(4);
    const [offset, setOffset] = useState(1);
    const [pagination, setPagination] = useState<any>({});
    const [editTournament, setEditTournament] = useState<any>(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [deleteID, setDeleteId] = useState<any>();
    const [viewModal, setViewModal] = useState(false);
    const [tournamentDetails, setTournamentDetails] = useState<any>();
    const [formValues, setFormValues] = useState({
        title: '', date: '', time: '', phonenumber: '', noOfTeams: '',
        organization_name: '', entry_fee: '', turf: '', team_size: '',
        tournament_format: '', match_length: '',
    });

    useEffect(() => {
        getAllTournaments(offset);
        getPartners();
    }, [offset]);

    const getAllTournaments = async (offsetVal: any) => {
        setTableLoading(true);
        try {
            const res = await getTournaments(limit, offsetVal);
            setTournaments(res.data.data);
            setPagination(res.data.pagination);
        } finally {
            setTableLoading(false);
        }
    };

    const openModal = async (item: any, data: any) => {
        setErrors({});
        if (data === 'update') {
            let res = await getTournamentById({ id: item.id });
            const row = res.data.data[0];
            const turfId = row?.turf_id;
            const turfStr = turfId == null || turfId === "" ? "" : typeof turfId === "object" ? JSON.stringify(turfId) : String(turfId);
            setFormValues({
                title: row?.title ?? "",
                date: row?.tournament_date ?? "",
                time: row?.tournament_time ?? "",
                phonenumber: row?.phonenumber != null ? String(row.phonenumber) : "",
                noOfTeams: row?.no_of_teams != null ? String(row.no_of_teams) : "",
                organization_name: row?.organization_name ?? "",
                entry_fee: row?.entry_fee != null ? String(row.entry_fee) : "",
                turf: turfStr,
                team_size: row?.team_size != null ? String(row.team_size) : "",
                tournament_format: row?.tournament_format ?? "",
                match_length: row?.match_length != null ? String(row.match_length) : "",
            });
            setBannerImage(normalizeBannerImages(row?.image));
            setEditTournament(row.id);
            setMode('update');
        } else {
            setFormValues({ title: '', date: '', time: '', phonenumber: '', noOfTeams: '', organization_name: '', entry_fee: '', turf: '', team_size: '', tournament_format: '', match_length: '' });
            setBannerImage([]);
            setMode('add');
        }
        setAddModal(true);
    };

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};
        const str = (v: unknown) => (v == null ? "" : String(v)).trim();
        if (!str(formValues.title)) newErrors.title = "Title is required";
        if (!str(formValues.date)) newErrors.date = "Date is required";
        if (!str(formValues.time)) newErrors.time = "Time is required";
        const phone = str(formValues.phonenumber);
        if (!phone) newErrors.phonenumber = "Phone number is required";
        else if (!/^\d{10}$/.test(phone)) newErrors.phonenumber = "Use a 10-digit mobile number";
        if (!str(formValues.noOfTeams)) newErrors.noOfTeams = "Number of teams is required";
        if (!str(formValues.organization_name)) newErrors.organization_name = "Organization name is required";
        if (!str(formValues.entry_fee)) newErrors.entry_fee = "Entry fee is required";
        if (!str(formValues.team_size)) newErrors.team_size = "Team size is required";
        if (!str(formValues.tournament_format)) newErrors.tournament_format = "Tournament format is required";
        if (!str(formValues.match_length)) newErrors.match_length = "Match length is required";
        if (!str(formValues.turf)) newErrors.turf = "Select a turf";
        if (mode === "add" && (!bannerImage || bannerImage.length === 0)) newErrors.bannerImage = "Upload at least one tournament image";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleImages = async (e: any) => {
        const files = e.target.files;
        if (!files.length) return;
        const formData = new FormData();
        for (let i = 0; i < files.length; i++) formData.append('file', files[i], files[i].name);
        try {
            const response = await uploadFile(formData);
            setBannerImage(normalizeBannerImages(response.data.files));
            setErrors((e) => ({ ...e, bannerImage: "" }));
        } catch (error) {
            toast.error("Image upload failed");
        }
    };

    const handleInputChange = (e: any) => {
        const { name, value } = e.target;
        setFormValues({ ...formValues, [name]: value });
        setErrors({ ...errors, [name]: "" });
    };

    const getPartners = async () => {
        setPartnersLoading(true);
        try {
            const res = await getAllPartners();
            setPartners(res.data.data);
        } catch (error) {
        } finally {
            setPartnersLoading(false);
        }
    };

    const handleSubmit = async (event: any) => {
        event.preventDefault();
        if (!validateForm()) return;

        const turfRaw = String(formValues.turf ?? "").trim();
        let turf_id: number;
        try {
            const p = JSON.parse(turfRaw);
            turf_id = typeof p === "number" && !Number.isNaN(p) ? p : Number(p);
        } catch {
            turf_id = Number(turfRaw);
        }
        if (!Number.isFinite(turf_id)) {
            setErrors({ turf: "Choose a valid turf from the list." });
            return;
        }

        try {
            const commonBody = {
                title: formValues.title,
                tournament_date: formValues.date,
                tournament_time: formValues.time,
                phonenumber: formValues.phonenumber,
                no_of_teams: formValues.noOfTeams,
                organization_name: formValues.organization_name,
                entry_fee: formValues.entry_fee,
                turf_id,
                team_size: formValues.team_size,
                tournament_format: formValues.tournament_format,
                match_length: formValues.match_length,
                image: JSON.stringify(bannerImage),
            };
            if (mode === 'add') {
                await addTournament(commonBody);
                toast.success("Tournament created");
            } else {
                await updateTournament({ ...commonBody, id: editTournament });
                toast.success("Tournament updated");
                setBannerImage([]);
            }
            getAllTournaments(offset);
            setAddModal(false);
        } catch (error) {
            toast.error("Failed to save tournament");
        }
    };

    const handleView = async (data: any) => {
        let res = await getTournamentById({ id: data.id });
        setTournamentDetails(res.data.data[0]);
        setViewModal(true);
    };

    const handleDelete = async (data: any) => {
        setDeleteModal(true);
        setDeleteId(data.id);
    };

    const deleteTournament = async () => {
        try {
            await deleteTournamentDetails({ id: deleteID });
            toast.success("Tournament deleted");
            setDeleteModal(false);
            getAllTournaments(offset);
        } catch (err) {
            toast.error("Failed to delete tournament");
        }
    };

    const formatDate = (dateString: any) => {
        const date = new Date(dateString);
        return `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
    };

    return (
        <div className="w-full max-w-6xl mx-auto pb-8 space-y-6">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-foreground">Tournaments</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Scheduled events linked to turfs. Add, edit, or review tournament details.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => getAllTournaments(offset)}>
                        <RefreshCw className="h-4 w-4" />
                    </Button>
                    <Button size="sm" onClick={() => openModal([], "add")}>
                        <Plus className="h-4 w-4 mr-1" /> Add tournament
                    </Button>
                </div>
            </div>

            {/* Table card */}
            <Card className="overflow-hidden">
                {tableLoading ? (
                    <div className="flex min-h-[200px] items-center justify-center">
                        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-12">#</TableHead>
                                    <TableHead>Tournament</TableHead>
                                    <TableHead className="whitespace-nowrap">When</TableHead>
                                    <TableHead>Host turf</TableHead>
                                    <TableHead className="hidden md:table-cell">Org</TableHead>
                                    <TableHead className="text-right whitespace-nowrap">Entry</TableHead>
                                    <TableHead className="text-right w-36">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {tournaments?.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} className="text-center text-muted-foreground py-14">
                                            No tournaments yet.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    tournaments?.map((item: any, index: number) => {
                                        const formattedDate = format(new Date(item.tournament_date), "dd MMM yyyy");
                                        return (
                                            <TableRow key={item.id}>
                                                <TableCell className="text-muted-foreground tabular-nums">{(offset - 1) * limit + index + 1}</TableCell>
                                                <TableCell>
                                                    <div className="font-medium">{item.title}</div>
                                                    <div className="text-xs text-muted-foreground mt-0.5 tabular-nums">{item.phonenumber}</div>
                                                    <div className="text-xs text-muted-foreground md:hidden mt-0.5 truncate max-w-[12rem]">{item.organization_name}</div>
                                                </TableCell>
                                                <TableCell className="text-sm whitespace-nowrap">
                                                    <span className="tabular-nums">{formattedDate}</span>
                                                    <span className="text-muted-foreground mx-1">·</span>
                                                    <span>{item.tournament_time}</span>
                                                </TableCell>
                                                <TableCell className="text-sm max-w-[11rem] truncate">{item.turfName}</TableCell>
                                                <TableCell className="text-sm max-w-[10rem] truncate hidden md:table-cell">{item.organization_name}</TableCell>
                                                <TableCell className="text-sm text-right tabular-nums whitespace-nowrap">
                                                    {item.entry_fee != null && item.entry_fee !== "" ? `₹${item.entry_fee}` : <span className="text-muted-foreground">—</span>}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openModal(item, "update")}>
                                                        <Pencil className="h-4 w-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleView(item)}>
                                                        <Eye className="h-4 w-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => handleDelete(item)}>
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </div>
                )}
                {!tableLoading && pagination?.last_page > 1 && (
                    <div className="border-t bg-muted/20 px-4 py-3">
                        <TablePagination
                            currentPage={offset}
                            totalPages={pagination?.last_page ?? 1}
                            onPageChange={(page) => setOffset(page)}
                        />
                    </div>
                )}
            </Card>

            {/* Add/Edit dialog */}
            <Dialog open={addModal} onOpenChange={setAddModal}>
                <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto p-0">
                    <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b bg-background/95 backdrop-blur-sm px-6 py-4">
                        <div className="min-w-0">
                            <DialogTitle>{mode === "add" ? "New tournament" : "Edit tournament"}</DialogTitle>
                            <DialogDescription className="mt-1">
                                {mode === "add"
                                    ? "Publish date, venue, cricket format, and visuals for players."
                                    : "Update details below. Leave images unchanged unless you upload new files."}
                            </DialogDescription>
                        </div>
                        <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => setAddModal(false)}>
                            <X className="h-4 w-4" />
                        </Button>
                    </div>

                    <div className="px-6 py-5 space-y-8">
                        {/* Basics */}
                        <section>
                            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground border-b pb-2 mb-4">Basics</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-1.5 sm:col-span-2">
                                    <Label>Tournament title</Label>
                                    <Input name="title" placeholder="e.g. Sunday Premier League" value={formValues.title} onChange={handleInputChange} />
                                    {errors.title && <p className="text-destructive text-xs">{errors.title}</p>}
                                </div>
                                <div className="space-y-1.5 sm:col-span-2">
                                    <Label>Organization</Label>
                                    <Input name="organization_name" placeholder="Organizer or club name" value={formValues.organization_name} onChange={handleInputChange} />
                                    {errors.organization_name && <p className="text-destructive text-xs">{errors.organization_name}</p>}
                                </div>
                                <div className="space-y-1.5">
                                    <Label>Contact phone</Label>
                                    <Input type="tel" name="phonenumber" placeholder="10-digit mobile" value={formValues.phonenumber} onChange={handleInputChange} />
                                    {errors.phonenumber && <p className="text-destructive text-xs">{errors.phonenumber}</p>}
                                </div>
                                <div className="space-y-1.5">
                                    <Label>Entry fee (₹)</Label>
                                    <Input name="entry_fee" placeholder="e.g. 500" value={formValues.entry_fee} onChange={handleInputChange} />
                                    {errors.entry_fee && <p className="text-destructive text-xs">{errors.entry_fee}</p>}
                                </div>
                            </div>
                        </section>

                        {/* Schedule & Venue */}
                        <section>
                            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground border-b pb-2 mb-4">Schedule & venue</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <Label>Date</Label>
                                    <DatePicker
                                        value={formValues.date && isValid(parseISO(formValues.date)) ? parseISO(formValues.date) : undefined}
                                        onChange={(date) => {
                                            setFormValues({ ...formValues, date: date ? format(date, 'yyyy-MM-dd') : '' });
                                            setErrors({ ...errors, date: "" });
                                        }}
                                        className="w-full"
                                    />
                                    {errors.date && <p className="text-destructive text-xs">{errors.date}</p>}
                                </div>
                                <div className="space-y-1.5">
                                    <Label>Start time</Label>
                                    <TimePicker
                                        value={formValues.time}
                                        onChange={(v) => {
                                            setFormValues({ ...formValues, time: v });
                                            setErrors({ ...errors, time: "" });
                                        }}
                                        className="w-full"
                                    />
                                    {errors.time && <p className="text-destructive text-xs">{errors.time}</p>}
                                </div>
                                <div className="space-y-1.5 sm:col-span-2">
                                    <Label>Host turf</Label>
                                    <select
                                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                        value={formValues.turf}
                                        disabled={partnersLoading}
                                        onChange={(e) => { setFormValues({ ...formValues, turf: e.target.value }); setErrors({ ...errors, turf: "" }); }}
                                    >
                                        <option value="">{partnersLoading ? "Loading venues…" : "Select partner turf"}</option>
                                        {partners.map((item: any) => (
                                            <option key={item.id} value={item.id}>{item.name}</option>
                                        ))}
                                    </select>
                                    {errors.turf && <p className="text-destructive text-xs">{errors.turf}</p>}
                                </div>
                            </div>
                        </section>

                        {/* Cricket format */}
                        <section>
                            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground border-b pb-2 mb-4">Cricket format</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <Label>Format</Label>
                                    <Input name="tournament_format" placeholder="e.g. T20, Round robin" value={formValues.tournament_format} onChange={handleInputChange} />
                                    {errors.tournament_format && <p className="text-destructive text-xs">{errors.tournament_format}</p>}
                                </div>
                                <div className="space-y-1.5">
                                    <Label>Match length (overs)</Label>
                                    <Input name="match_length" placeholder="e.g. 20" value={formValues.match_length} onChange={handleInputChange} />
                                    {errors.match_length && <p className="text-destructive text-xs">{errors.match_length}</p>}
                                </div>
                                <div className="space-y-1.5">
                                    <Label>Number of teams</Label>
                                    <Input type="number" min={1} name="noOfTeams" placeholder="e.g. 8" value={formValues.noOfTeams} onChange={handleInputChange} />
                                    {errors.noOfTeams && <p className="text-destructive text-xs">{errors.noOfTeams}</p>}
                                </div>
                                <div className="space-y-1.5">
                                    <Label>Squad size (players)</Label>
                                    <Input name="team_size" placeholder="Players per team" value={formValues.team_size} onChange={handleInputChange} />
                                    {errors.team_size && <p className="text-destructive text-xs">{errors.team_size}</p>}
                                </div>
                            </div>
                        </section>

                        {/* Images */}
                        <section>
                            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground border-b pb-2 mb-4">Images</h4>
                            <p className="text-sm text-muted-foreground mb-3">
                                {mode === "add" ? "At least one image is required." : "Optional when editing—existing images stay unless you replace them."}
                            </p>
                            <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handleImages}
                                className="block w-full text-sm text-muted-foreground file:mr-4 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-primary-foreground hover:file:bg-primary/90"
                            />
                            {bannerImage && bannerImage.length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-3">
                                    {bannerImage.map((url, index) => (
                                        <img
                                            key={`${url?.imageUrl ?? index}-${index}`}
                                            src={url?.imageUrl ?? ""}
                                            alt=""
                                            className="h-20 w-28 object-cover rounded-lg border bg-muted/40"
                                            onError={(e) => { e.currentTarget.src = "/wood-blog-placeholder.jpg"; }}
                                        />
                                    ))}
                                </div>
                            )}
                            {errors.bannerImage && <p className="text-destructive text-xs mt-2">{errors.bannerImage}</p>}
                        </section>
                    </div>

                    <div className="border-t bg-muted/20 px-6 py-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                        <Button variant="ghost" onClick={() => setAddModal(false)}>Cancel</Button>
                        <Button onClick={handleSubmit}>{mode === "add" ? "Create tournament" : "Save changes"}</Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Delete confirmation dialog */}
            <Dialog open={deleteModal} onOpenChange={setDeleteModal}>
                <DialogContent className="max-w-sm">
                    <DialogHeader className="items-center text-center">
                        <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-2" />
                        <DialogTitle>Delete this tournament?</DialogTitle>
                        <DialogDescription>This cannot be undone.</DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex-row justify-center gap-2 sm:justify-center">
                        <Button variant="outline" onClick={() => setDeleteModal(false)}>Cancel</Button>
                        <Button variant="destructive" onClick={deleteTournament}>Delete</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* View dialog */}
            <Dialog open={viewModal} onOpenChange={setViewModal}>
                {tournamentDetails && (
                    <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto p-0">
                        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b bg-background/95 backdrop-blur-sm px-6 py-4">
                            <div className="min-w-0">
                                <p className="text-xs font-semibold uppercase tracking-wider text-primary/80">Tournament</p>
                                <DialogTitle className="text-xl mt-0.5">{tournamentDetails.title}</DialogTitle>
                                <DialogDescription className="mt-1">{tournamentDetails.organization_name}</DialogDescription>
                            </div>
                            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => setViewModal(false)}>
                                <X className="h-4 w-4" />
                            </Button>
                        </div>
                        <div className="px-6 py-5 space-y-6">
                            {viewGalleryImages(tournamentDetails.image).length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                    {viewGalleryImages(tournamentDetails.image).map((src, index) => (
                                        <img
                                            key={`${src}-${index}`}
                                            src={src}
                                            alt=""
                                            className="h-24 w-36 sm:h-28 sm:w-40 object-cover rounded-xl border bg-muted/40"
                                            onError={(e) => { e.currentTarget.src = "/wood-blog-placeholder.jpg"; }}
                                        />
                                    ))}
                                </div>
                            )}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {[
                                    { label: "When", value: `${formatDate(tournamentDetails.tournament_date)} · ${tournamentDetails.tournament_time}` },
                                    { label: "Host turf", value: tournamentDetails.turfName ?? "—" },
                                    { label: "Contact", value: tournamentDetails.phonenumber },
                                    { label: "Entry fee", value: tournamentDetails.entry_fee != null && tournamentDetails.entry_fee !== "" ? `₹${tournamentDetails.entry_fee}` : "—" },
                                ].map((card) => (
                                    <div key={card.label} className="rounded-xl border bg-background p-4">
                                        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{card.label}</p>
                                        <p className="text-sm font-semibold mt-1 truncate">{card.value}</p>
                                    </div>
                                ))}
                            </div>
                            <div className="rounded-xl border overflow-hidden">
                                <Table>
                                    <TableBody className="text-sm">
                                        {[
                                            { label: "Format", value: tournamentDetails.tournament_format ?? "—" },
                                            { label: "Overs", value: tournamentDetails.match_length ?? "—" },
                                            { label: "Teams", value: tournamentDetails.no_of_teams ?? "—" },
                                            { label: "Squad size", value: tournamentDetails.team_size ?? "—" },
                                        ].map((row) => (
                                            <TableRow key={row.label}>
                                                <TableHead className="w-40 bg-muted/40 font-medium">{row.label}</TableHead>
                                                <TableCell className="tabular-nums">{row.value}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </div>
                    </DialogContent>
                )}
            </Dialog>
        </div>
    );
}

export default Tournament;
